"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateCourseList = void 0;
const _ = require("lodash");
/**
 * Updates course lists to match the desired number of rounds
 *
 * This function ensures that each course list (top_rated, best_sellers, best_value, custom)
 * has exactly the number of items specified by number_of_rounds.
 *
 * If a list is too short, it's padded with repeated items from the beginning of the list.
 * If a list is too long, it's truncated to the desired length.
 *
 * @param update_obj - The course lists object to be updated
 * @param number_of_rounds - The desired number of items in each list
 * @param course_lists - The source course lists to draw additional items from
 * @returns Object containing the updated course lists and a flag indicating if changes were made
 */
const updateCourseList = (update_obj, number_of_rounds, course_lists) => {
    let update_occurred = false;
    // Extract lists from the source course_lists
    const { top_rated = [], best_sellers = [], best_value = [], custom = [], } = course_lists;
    // Create a copy of the update object to avoid mutating the original
    const updated = { ...update_obj };
    // Define list types to process
    const listTypes = [
        { name: "top_rated", source: top_rated },
        { name: "best_sellers", source: best_sellers },
        { name: "best_value", source: best_value },
        { name: "custom", source: custom },
    ];
    // Process each list type (top_rated, best_sellers, best_value, custom)
    listTypes.forEach(({ name, source }) => {
        // Using type assertion to help TypeScript understand this is a valid key
        const currentList = updated[name] || [];
        const currentLength = currentList.length;
        // Case 1: List is shorter than desired - add more items
        if (currentLength < number_of_rounds && source) {
            // If we need more items than exist in the source, we'll need to repeat items
            // Starting from where we left off in the current list
            const startIndex = currentLength % source.length;
            const additionalItemsNeeded = number_of_rounds - currentLength;
            // Create deep clones of items to avoid reference issues
            const additionalItems = [];
            // Handle case where source might be empty
            if (source.length > 0) {
                // We might need to cycle through the source list multiple times
                for (let i = 0; i < additionalItemsNeeded; i++) {
                    const sourceIndex = (startIndex + i) % source.length;
                    additionalItems.push(_.cloneDeep(source[sourceIndex]));
                }
            }
            const newList = [...currentList, ...additionalItems];
            updated[name] = newList;
            // Only mark as updated if items were actually added
            if (additionalItems.length > 0) {
                update_occurred = true;
            }
        }
        // Case 2: List is longer than desired - truncate
        else if (currentLength > number_of_rounds) {
            const newList = currentList.slice(0, number_of_rounds);
            updated[name] = newList;
            // Only mark as updated if the list was actually truncated
            if (currentLength > number_of_rounds) {
                update_occurred = true;
            }
        }
        // If currentLength === number_of_rounds, no action needed
    });
    // Handle custom list separately as it may contain nulls and doesn't repeat items
    const currentCustomLength = (updated.custom || []).length;
    if (currentCustomLength !== number_of_rounds) {
        // Create a new array based on the current custom list
        const customCourses = [...(updated.custom || [])];
        // Resize the array to the desired length
        // If shorter, JavaScript will fill with undefined
        // If longer, it will truncate
        customCourses.length = number_of_rounds;
        updated.custom = customCourses;
        update_occurred = true;
    }
    return {
        update_occurred,
        update_obj: updated,
    };
};
exports.updateCourseList = updateCourseList;
